import React from "react";
import { DispatchProp } from "react-redux";
import { QuestionModel } from "../../models/QuestionModel";
import { ControlProps } from "./BaseControl";
import { BaseGridControl } from "./BaseGridControl";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import {
    getSettingBoolean,
    getSettingString,
    getSettingNumber,
    hasSetting,
} from "../../models/utilities/settingUtilities";
import { Signal } from "../../signals/index";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { getRendererProps } from "../renderers/BaseRenderer";
import {
    isHeaderRow,
    variableForCell,
    hasOpenEnd,
    openEndVariableName,
} from "../../models/utilities/gridUtilities";
import { Piping } from "../../piping";
import { isNullOrEmpty, removeHtmlTags } from "../../utils/index";
import { isEqual } from "../../utils/lodash";
import { PoliteStatusPortal } from "../shared/PoliteStatusPortal";
import { AxisModel } from "../../models/AxisModel";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import { RecallType } from "../../piping/RecallType";
import { getNumeral, formatNumeric } from "../../utils/Number";
import { __getStore__ } from "../../redux/store";
import GridRenderer from "../renderers/v2/GridRenderer";
import { ElementWithSettings } from "../../models/ElementWithSettings";
import { MentionModel } from "../../models/MentionModel";
import { VariableModel } from "../../models/VariableModel";
import { translate } from "../../models/utilities/translationUtilities";
import { isQuestionValidatedServerSide } from "../../models/utilities/validationUtilities";
import { QuestionType } from "../../models/enums/QuestionType";

interface Props extends ControlProps, DispatchProp {}

interface State {
    validationResult?: ValidationResultModel;
    openEndValue: { [key: string]: string };
    /** Debounced text for PoliteStatusPortal (running total vs target). */
    srTotalAnnouncement?: string;
}

export class RunningTotalControl extends BaseGridControl<Props, State> {
    validateSignal = new Signal<ValidationOptionsModel, ValidationResultModel>();
    tableRef: HTMLTableElement;

    private announceTimer: number | undefined;

    private lastAnnouncementDigest: string | undefined;
    private lastColumnTotals: number[] | undefined;

    constructor(props: Props) {
        super(props);
        this.state = { openEndValue: {}, srTotalAnnouncement: "" };
    }

    initializeQuestion(question: QuestionModel, props: Props): void {
        super.initializeQuestion(question, props);
        this.lastAnnouncementDigest = undefined;
        this.lastColumnTotals = undefined;
        this.setState({
            openEndValue: {},
            validationResult: null,
            srTotalAnnouncement: "",
        });
        this.initializeOpenEnd(question, props);
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        this.initializeOpenEnd(nextProps.question, nextProps);
        super.UNSAFE_componentWillReceiveProps(nextProps);
    }

    override componentDidMount() {
        super.componentDidMount();
        this.queueRunningTotalAnnouncement();
    }

    override componentDidUpdate(prevProps: Props, prevState: State) {
        super.componentDidUpdate(prevProps, prevState);
        this.queueRunningTotalAnnouncement();
        if (this.state.validationResult && !isEqual(prevProps.mentions, this.props.mentions)) {
            this.setState({ validationResult: null });
        }
    }

    override componentWillUnmount() {
        if (this.announceTimer != null) {
            window.clearTimeout(this.announceTimer);
            this.announceTimer = undefined;
        }
        super.componentWillUnmount();
    }

    private getRunningTotalAnnouncementDigest(): string {
        const { question } = this.props;
        return question.variableNames
            .map((name) => this.getMentionValue(this.props, name))
            .join("\u0001");
    }

    private queueRunningTotalAnnouncement() {
        const digest = this.getRunningTotalAnnouncementDigest();
        if (digest === this.lastAnnouncementDigest) {
            return;
        }
        this.lastAnnouncementDigest = digest;
        if (this.announceTimer != null) {
            window.clearTimeout(this.announceTimer);
        }
        this.announceTimer = window.setTimeout(() => {
            this.announceTimer = undefined;
            this.setState({ srTotalAnnouncement: this.getRunningTotalAnnouncement() });
        }, 400);
    }

    private getRunningTotalAnnouncement(): string {
        const { language, questionnaire, sequencedColumns } = this.props;
        const runningTotalLabel =
            translate(questionnaire, "RunningTotal", language) || "Running total";
        const currentColumnTotals = sequencedColumns.map((column) => this.columnRunningTotal(column));
        const prevTotals = this.lastColumnTotals;

        if (prevTotals === undefined) {
            this.lastColumnTotals = currentColumnTotals;
            const parts: string[] = [];
            sequencedColumns.forEach((column, index) => {
                if (!column.variables.length) {
                    return;
                }
                const formatted = this.formatNumeric(currentColumnTotals[index], column);
                const rawHeading = translate(column, "LeftLabel", language) || translate(column, "TEXT", language);
                const heading = rawHeading ? `${removeHtmlTags(rawHeading)}. ` : "";
                parts.push(`${heading}${runningTotalLabel} ${formatted}.`);
            });
            return parts.join(" ");
        }

        const changedColumnIndexes = currentColumnTotals
            .map((total, index) => ({
                index,
                hasChanged: prevTotals[index] !== total,
            }))
            .filter((entry) => entry.hasChanged)
            .map((entry) => entry.index);
        const changedColumnIndex = changedColumnIndexes[0];

        this.lastColumnTotals = currentColumnTotals;

        if (changedColumnIndex === undefined || changedColumnIndex < 0) {
            return "";
        }
        const column = sequencedColumns[changedColumnIndex];
        const formatted = this.formatNumeric(currentColumnTotals[changedColumnIndex], column);
        const rawHeading = translate(column, "LeftLabel", language) || translate(column, "TEXT", language);
        const heading = rawHeading ? `${removeHtmlTags(rawHeading)}. ` : "";
        return `${heading}${runningTotalLabel} ${formatted}.`;
    }

    getVariableNames = (axes: AxisModel[]): string[] =>
        axes
            .map((axis) => axis.variables.map((variable) => variable.name))
            .reduce((acc, next) => [...acc, ...next], [])
            .reduce((acc, next) => (acc.includes(next) ? acc : [...acc, next]), []);

    getAxesVariablesToClear = (prevAxes: AxisModel[], currentAxes: AxisModel[]): string[] => {
        const prevAxesVariables = this.getVariableNames(prevAxes);
        const currentAxesVariables = this.getVariableNames(currentAxes);
        const axesVariablesToClear = prevAxesVariables.filter(
            (variable) => !currentAxesVariables.includes(variable),
        );
        return axesVariablesToClear;
    };

    getOpenEndVariablesToClear = (prevRows: AxisModel[], currentRows: AxisModel[]): string[] => {
        const prevOpenEnds = prevRows
            .filter((row) => !isNullOrEmpty(row.settings.OpenEndVariable))
            .map((row) => row.settings.OpenEndVariable);
        const currentOpenEnds = currentRows
            .filter((row) => !isNullOrEmpty(row.settings.OpenEndVariable))
            .map((row) => row.settings.OpenEndVariable);
        const openEndVariablesToClear = prevOpenEnds.filter(
            (variable) => !currentOpenEnds.includes(variable),
        );
        return openEndVariablesToClear;
    };

    getRemovedVariableNames = (prevProps: Props, props: Props): string[] => {
        const columnVariablesToClear = this.getAxesVariablesToClear(
            prevProps.sequencedColumns,
            props.sequencedColumns,
        );
        const rowVariablesToClear = this.getAxesVariablesToClear(
            prevProps.sequencedRows,
            props.sequencedRows,
        );
        const openEndVariablesToClear = this.getOpenEndVariablesToClear(
            prevProps.sequencedRows,
            props.sequencedRows,
        );
        const variablesToClear = [...columnVariablesToClear, ...rowVariablesToClear]
            .reduce((acc, next) => (acc.includes(next) ? acc : [...acc, next]), [])
            .concat(openEndVariablesToClear);
        return variablesToClear;
    };

    didUpdateVariables(sequencedVariables: VariableModel[], prevProps: Props) {
        const variablesToClear = this.getRemovedVariableNames(prevProps, this.props);
        variablesToClear.forEach((variableName) =>
            this.setMentions(this.props, variableName, (mentions) => mentions.clear()),
        );
    }

    initializeOpenEnd(question: QuestionModel, props: Props) {
        this.setState((prevState) => {
            const newState = { ...prevState };
            question.rows
                .filter((row) => hasOpenEnd(row))
                .forEach((row) => {
                    const openEndName = openEndVariableName(row);
                    const mentions = this.getMentions(props, openEndName);
                    const mention = mentions != null ? mentions[0] : undefined;
                    if (mention !== undefined && !isNullOrEmpty(mention.openEnd)) {
                        newState.openEndValue[openEndName] = mention.openEnd;
                    } else {
                        newState.openEndValue[openEndName] = "";
                    }
                });
            return newState;
        });
    }

    onOpenEndChange = (answer: any, event: any) => {
        const { value } = event.target;
        this.setState((prevState) => {
            const newState = { ...prevState };
            newState.openEndValue = {
                ...newState.openEndValue,
                [openEndVariableName(answer)]: this.truncateOpenEnd(answer, value),
            };
            return newState;
        });
    };

    onOpenEndBlur = (answer: any) => {
        const openEndName = openEndVariableName(answer);
        const openEnd = this.state.openEndValue[openEndName];
        if (isNullOrEmpty(openEnd)) {
            this.setMentions(this.props, openEndName, (mentions) => mentions.clear());
        } else {
            this.setMentions(this.props, openEndName, (mentions) =>
                mentions.set(getSettingString(answer, "OpenEndAnswerCode", "0"), openEnd.trim()),
            );
        }
    };

    onColumnDisplayRunningTotal = (column: AxisModel) => {
        const total = this.columnRunningTotal(column);
        return this.formatNumeric(total, column);
    };

    bodyRows = () => {
        const { sequencedRows = [] } = this.props;
        return sequencedRows.filter((row) => !isHeaderRow(row));
    };

    columnRunningTotal(column: AxisModel) {
        const rows = this.bodyRows();
        const total = rows
            .map((row) => {
                const variable = variableForCell(row, column);
                const value = this.getMentionValue(this.props, variable.name);
                return getNumeral(value).value();
            })
            .reduce((a: number, b: number) => a + b, 0);
        return total;
    }

    formatNumeric(value: string | number, column: AxisModel): string {
        const decimals = getSettingNumber(column, "NumberOfDecimals");
        return formatNumeric(
            value,
            decimals,
            this.props.language,
            false,
            !hasSetting(column, "AutoFormatting") || getSettingBoolean(column, "AutoFormatting"),
        );
    }

    onReset = (question: QuestionModel) => {
        question.variableNames.forEach((v) =>
            this.setMentions(this.props, v, (mentions) => mentions.clear()),
        );
    };

    validateAnswerRequired = (
        rows: AxisModel[],
        columns: AxisModel[],
        customError: string,
        validation: ValidationBuilder,
    ): ValidationBuilder => {
        const errorKey = !isNullOrEmpty(customError) ? customError : "IncompleteAnswer";
        columns.forEach((column) => {
            const isColumnRequired = this.isAxisAnswerRequired(column);
            if (!isColumnRequired) {
                return;
            }
            rows.forEach((row) => {
                const isRowRequired = this.isAxisAnswerRequired(row);
                const variable = variableForCell(row, column);
                if (!isRowRequired || variable === undefined) {
                    return;
                }
                const hasMentions = this.variableHasMentions(variable);
                if (!hasMentions) {
                    validation.fail(variable.name, errorKey);
                }
            });
        });
        return validation;
    };

    hasValidNumericValue = (mention: MentionModel) =>
        mention && !Number.isNaN(parseFloat(mention.value));

    variableHasMentions = (variable: VariableModel) =>
        this.getMentions(this.props, variable.name).filter(this.hasValidNumericValue).length;

    axisHasMentions = (axis: AxisModel) => axis.variables.some(this.variableHasMentions);

    validateColumnRunningTotal = (
        column: AxisModel,
        customError: string,
        validation: ValidationBuilder,
    ): ValidationBuilder => {
        const hasMentionsSet = this.axisHasMentions(column);
        if (!hasMentionsSet) {
            return validation;
        }

        const { language } = this.props;
        const min = this.getColumnValueSetting(column, "MinValue");
        const max = this.getColumnValueSetting(column, "MaxValue");
        const total = this.columnRunningTotal(column);

        const hasMax = max !== undefined;
        const hasMin = min !== undefined;

        const invalidMax = hasMax && total > max;
        const invalidMin = hasMin && total < min;
        const outOfBounds = hasMax && hasMin && (invalidMax || invalidMin);

        let columnError = "";
        if (invalidMax) {
            columnError = translate(column, "UpperBoundNotFulfilledMessage", language);
        } else if (invalidMin) {
            columnError = translate(column, "LowerBoundNotFulfilledMessage", language);
        }

        let errorKey = "";

        if (outOfBounds) {
            errorKey = customError || "OutOfBounds";
        } else if (invalidMax) {
            errorKey = customError || "UpperBoundNotFulfilled";
        } else if (invalidMin) {
            errorKey = customError || "LowerBoundNotFulfilled";
        }

        if (!isNullOrEmpty(errorKey)) {
            column.variables.forEach((variable) =>
                validation.fail(
                    variable.name,
                    !isNullOrEmpty(columnError) ? "CustomErrorMessage" : errorKey,
                    {
                        min: hasMin ? String(min) : undefined,
                        max: hasMax ? String(max) : undefined,
                    },
                    columnError,
                ),
            );
        }
        return validation;
    };

    validateRowOpenEnd = (
        row: AxisModel,
        openEndValue: { [key: string]: string },
        customError: string,
        validation: ValidationBuilder,
    ): ValidationBuilder => {
        const hasMentionsSet = this.axisHasMentions(row);
        const rowHasOpenEnd = hasOpenEnd(row);
        const isRowOpenEndRequired = getSettingBoolean(row, "EnforceOpenEnd");
        if (!hasMentionsSet || !rowHasOpenEnd || !isRowOpenEndRequired || !row.openEndActive) {
            return validation;
        }

        const rowOpenEndVariableName = openEndVariableName(row);
        const hasOpenEndMentionSet =
            openEndValue !== undefined && !isNullOrEmpty(openEndValue[rowOpenEndVariableName]);
        if (!hasOpenEndMentionSet) {
            const errorKey = !isNullOrEmpty(customError) ? customError : "IncompleteAnswer";
            validation.fail(rowOpenEndVariableName, errorKey);
        }

        const minLength = getSettingNumber(row, "MinOpenEndLength");
        const isOpenEndValueMinimumLengthValid =
            minLength === undefined ||
            (hasOpenEndMentionSet && openEndValue[rowOpenEndVariableName].length >= minLength);
        if (!isOpenEndValueMinimumLengthValid) {
            const errorKey = !isNullOrEmpty(customError) ? customError : "InvalidCharactersMin";
            validation.fail(rowOpenEndVariableName, errorKey, {
                min: String(minLength),
            });
        }

        return validation;
    };

    private buildAndPersistValidation(
        validation: ValidationBuilder,
        options?: ValidationOptionsModel,
    ): ValidationResultModel {
        const result = validation.build();
        if (!options?.silent) {
            this.setState((prevState) => ({ ...prevState, validationResult: result }));
        }
        return result;
    }

    validate(options?: ValidationOptionsModel): ValidationResultModel {
        const {
            questionnaire,
            question,
            sequencedColumns,
            sequencedRows,
            language,
            useClientValidation,
        } = this.props;
        const validation = new ValidationBuilder();
        question.variables.forEach(this.removeUnavailableMentions);

        if (isQuestionValidatedServerSide(question.type, useClientValidation)) {
            return this.buildAndPersistValidation(validation, options);
        }

        if (this.isSoftPrompt(this.props) && !this.hasMentions(this.props)) {
            const result = this.validateSoftPrompt(this.props);
            if (!options?.silent) {
                this.setState((prevState) => ({ ...prevState, validationResult: result }));
            }
            return result;
        }
        const customError = translate(question, "CustomErrorMessage", language);

        if (this.isAnswerRequired(questionnaire, question)) {
            // Per-row/column optional cells are handled inside validateAnswerRequired via
            // isAxisAnswerRequired; question-level gate preserves INPUT_REQUIRED / SoftPrompt behaviour.
            this.validateAnswerRequired(sequencedRows, sequencedColumns, customError, validation);
        }
        sequencedRows.forEach((row) => {
            this.validateRowOpenEnd(row, this.state.openEndValue, customError, validation);
        });
        sequencedColumns.forEach((column) => {
            this.validateColumnRunningTotal(column, customError, validation);
        });
        return this.buildAndPersistValidation(validation, options);
    }

    getColumnValueSetting(column: ElementWithSettings, setting: "MinValue" | "MaxValue") {
        const { interview } = __getStore__().getState();
        const value = getSettingString(column, setting);
        const pipedValue = Piping.evaluateRecalls(interview, value, RecallType.Code);
        const parsedValue = parseInt(pipedValue, 10);
        return !Number.isNaN(parsedValue) ? parsedValue : undefined;
    }

    renderQuestion(question: QuestionModel): JSX.Element {
        if (question.type === QuestionType.RunningTotal) {
            question.sequencedRowVariableIds = this.props.sequencedRows
                .filter((row) => row.variables.length)
                .map((row) => row.variables.map((v) => v.id).toString());
            question.sequencedColumnVariableIds = this.props.sequencedColumns
                .filter((column) => column.variables.length)
                .map((column) => column.variables.map((v) => v.id).toString());
        }

        return (
            <>
                <PoliteStatusPortal>{this.state.srTotalAnnouncement}</PoliteStatusPortal>
                <GridRenderer
                    {...getRendererProps(this.props)}
                    columns={this.props.sequencedColumns}
                    rows={this.props.sequencedRows}
                    validateSignal={this.validateSignal}
                    validationResult={this.state.validationResult}
                    shouldHideCell={this.onShouldHideCell}
                    getColumnRunningTotal={this.onColumnDisplayRunningTotal}
                    openEndValue={this.state.openEndValue}
                    onOpenEndBlur={this.onOpenEndBlur}
                    onOpenEndChange={this.onOpenEndChange}
                    showSoftPrompt={this.props.showSoftPrompt}
                    matrix={this.props.matrix}
                    onReset={() => this.onReset(question)}
                />
            </>
        );
    }
}
